import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Modal from "components/modal";
import InputField from "components/fields/InputField";
import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import { MdModeEditOutline } from "react-icons/md";
import { updateOrderItem } from "store/orderSlice";
import { mediaUpload } from "api/usersApi";
import { showToast } from "components/toast";
import { ORDER_TYPE } from "constants";
import FileInputField from "components/fields/FileInputField";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function EditItemModal({
  data,
  refreshList,
  orderType = ORDER_TYPE.BASED_ON_DONE,
}) {
  const dispatch = useDispatch();
  const { order: { draft = {} } = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );
  const { updateOrderItemLoading } = useSelector((state) => state.orders);
  const [isOpen, setIsOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState(data?.picture || null);

  const defaultValuesBased = {
    name: data?.name || "",
    description: data?.description || "",
    quantity: data?.totalQty || "",
    ...(draft.itemPrice ? { price: data?.price || "" } : {}),
    imgFile: null,
  };

  const defaultValuesCarrying = {
    name: data?.name || "",
    description: data?.description || "",
    marka: data?.marka || "",
    ctn: data?.ctn || "",
    qty: data?.qty || "",
    totalQty: data?.totalQty || "",
    rate: data?.rate || "",
    totalAmount: data?.totalAmount || "",
    cbm: data?.cbm || "",
    totalCBM: data?.totalCBM || "",
    kg: data?.kg || "",
    totalKg: data?.totalKg || "",
    imgFile: null,
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues:
      orderType === ORDER_TYPE.CARRYING
        ? defaultValuesCarrying
        : defaultValuesBased,
    mode: "onBlur",
  });

  // Open modal and reset form
  const openModal = () => {
    reset(
      orderType === ORDER_TYPE.CARRYING
        ? defaultValuesCarrying
        : defaultValuesBased
    );
    setPreviewImg(data?.picture || null);
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  // Watch fields for auto-calculation
  const wCtn = watch("ctn");
  const wQty = watch("qty");
  const wRate = watch("rate");
  const wCbm = watch("cbm");
  const wKg = watch("kg");

  useEffect(() => {
    if (orderType !== ORDER_TYPE.CARRYING) return;
    setValue("totalQty", num(wCtn) * num(wQty));
    setValue("totalAmount", num(wCtn) * num(wQty) * num(wRate));
    setValue("totalCBM", num(wCtn) * num(wCbm));
    setValue("totalKg", num(wCtn) * num(wKg));
  }, [wCtn, wQty, wRate, wCbm, wKg, setValue, orderType]);

  const handleSave = async (formData) => {
    try {
      let imageUrl = previewImg;

      // Upload new image if selected
      if (formData.imgFile?.[0]) {
        const res = await mediaUpload(formData.imgFile[0]);
        if (res?.statuscode === 200 || res?.statuscode === 201) {
          imageUrl = res.data;
        } else {
          showToast.error("Image upload failed");
          return;
        }
      }

      const updatedItem =
        orderType === ORDER_TYPE.CARRYING
          ? {
              ...data,
              name: formData.name,
              description: formData.description,
              marka: formData.marka,
              ctn: num(formData.ctn),
              qty: num(formData.qty),
              totalQty: num(formData.totalQty),
              rate: num(formData.rate),
              totalAmount: num(formData.totalAmount),
              cbm: num(formData.cbm),
              totalCBM: num(formData.totalCBM),
              kg: num(formData.kg),
              totalKg: num(formData.totalKg),
              picture: imageUrl,
            }
          : {
              ...data,
              name: formData.name,
              description: formData.description,
              totalQty: num(formData.quantity),
              ...(draft.itemPrice ? { price: num(formData.price) } : {}),
              picture: imageUrl,
            };

      const response = await dispatch(updateOrderItem(updatedItem));

      if (
        response?.payload?.statuscode === 200 ||
        response?.payload?.statuscode === 201
      ) {
        refreshList?.();
        closeModal();
      } else {
      }
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const ReadonlyBox = ({ label, value }) => (
    <div>
      {label && (
        <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
          {label}
        </label>
      )}
      <div className="mt-0.5 flex h-9 w-full rounded-md border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:text-white">
        {value ?? 0}
      </div>
    </div>
  );

  return (
    <>
      <Button type="button" variant="default" onClick={openModal}>
        <MdModeEditOutline className="h-4 w-4" />
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        className={`${
          orderType === ORDER_TYPE.CARRYING ? "!max-w-6xl" : "!max-w-3xl"
        }`}
      >
        <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
          Edit Item
        </h2>

        <div className="custom-scrollbar mt-5 max-h-[70vh] overflow-y-auto">
          <form onSubmit={handleSubmit(handleSave)} className="mt-4">
            {orderType === ORDER_TYPE.CARRYING ? (
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                  {/* Image Preview + File Input */}

                  {previewImg && (
                    <img
                      src={previewImg}
                      alt="Item"
                      className="h-20 w-20 rounded-md border border-gray-200 object-cover dark:border-white/10"
                    />
                  )}
                  <FileInputField
                    id="imgFile"
                    label="Upload Image*"
                    accept="image/*"
                    {...register("imgFile")}
                    onChange={(e) =>
                      setPreviewImg(URL.createObjectURL(e.target.files[0]))
                    }
                  />

                  <InputField
                    id="name"
                    label="Name*"
                    placeholder="Item name"
                    error={errors.name}
                    {...register("name", { required: "Name is required" })}
                  />
                  <InputField
                    id="description"
                    label="Description"
                    placeholder="Item description"
                    error={errors.description}
                    {...register("description")}
                  />
                  <InputField
                    id="marka"
                    label="Marka*"
                    placeholder="Brand / Marka"
                    error={errors.marka}
                    {...register("marka", { required: "Marka is required" })}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                  <InputField
                    id="ctn"
                    label="CTN*"
                    type="number"
                    error={errors.ctn}
                    {...register("ctn", {
                      required: "CTN is required",
                      valueAsNumber: true,
                      min: { value: 0, message: "Must be ≥ 0" },
                    })}
                  />
                  <InputField
                    id="qty"
                    label="QTY*"
                    type="number"
                    error={errors.qty}
                    {...register("qty", {
                      required: "QTY is required",
                      valueAsNumber: true,
                      min: { value: 0, message: "Must be ≥ 0" },
                    })}
                  />
                  <InputField
                    id="rate"
                    label="Rate*"
                    type="number"
                    step="0.01"
                    error={errors.rate}
                    {...register("rate", {
                      required: "Rate is required",
                      valueAsNumber: true,
                      min: { value: 0, message: "Must be ≥ 0" },
                    })}
                  />
                  <InputField
                    id="cbm"
                    label="CBM*"
                    type="number"
                    step="0.0001"
                    error={errors.cbm}
                    {...register("cbm", {
                      required: "CBM is required",
                      valueAsNumber: true,
                      min: { value: 0, message: "Must be ≥ 0" },
                    })}
                  />
                  <InputField
                    id="kg"
                    label="KG*"
                    type="number"
                    step="0.01"
                    error={errors.kg}
                    {...register("kg", {
                      required: "KG is required",
                      valueAsNumber: true,
                      min: { value: 0, message: "Must be ≥ 0" },
                    })}
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <ReadonlyBox label="Total QTY" value={watch("totalQty")} />
                  <ReadonlyBox
                    label="Total Amount"
                    value={watch("totalAmount")}
                  />
                  <ReadonlyBox label="Total CBM" value={watch("totalCBM")} />
                  <ReadonlyBox label="Total KG" value={watch("totalKg")} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {previewImg && (
                  <img
                    src={previewImg}
                    alt="Item"
                    className="h-20 w-20 rounded-md border border-gray-200 object-cover dark:border-white/10"
                  />
                )}
                <FileInputField
                  id="imgFile"
                  label="Upload Image*"
                  accept="image/*"
                  {...register("imgFile")}
                  onChange={(e) =>
                    setPreviewImg(URL.createObjectURL(e.target.files[0]))
                  }
                />

                <InputField
                  id="name"
                  label="Name*"
                  type="text"
                  error={errors.name}
                  {...register("name", { required: "Name is required" })}
                />
                <InputField
                  id="description"
                  label="Description"
                  error={errors.description}
                  {...register("description")}
                />
                <InputField
                  id="quantity"
                  label="Quantity*"
                  type="number"
                  error={errors.quantity}
                  {...register("quantity", {
                    required: "Quantity is required",
                  })}
                />
                {draft.itemPrice && (
                  <InputField
                    id="price"
                    label="Price*"
                    type="number"
                    error={errors.price}
                    {...register("price")}
                  />
                )}
              </div>
            )}

            <div className="col-span-2 my-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <LoaderButton type="submit" isLoading={updateOrderItemLoading}>
                Save Changes
              </LoaderButton>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}

export default EditItemModal;
